import { zodResolver } from "@hookform/resolvers/zod";
import { CheckIcon, Loader2Icon, ReceiptIcon } from "lucide-react";
import { Suspense, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import Pix from "@/assets/img/pix.png";
import { AnimatedLoading } from "@/components/animated-loading";
import { ClipboardButton } from "@/components/clipboard-button";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useRechargeMutation } from "../../hooks/mutations/use-recharge-mutation";
import { useListenRecharge } from "../../hooks/queries/use-listen-recharge";
import { useRechargeQuery } from "../../hooks/queries/use-recharge-query";
import { useRechargeDialogStore } from "../../hooks/stores/use-recharge-dialog";
import { RechargeInput } from "./recharge-input";
import { rechargeSchema, RechargeSchema } from "./schema";

export function RechargeDialog() {
  const { isOpen, setIsOpen, step } = useRechargeDialogStore();

  const currentStepTitle = useMemo(() => {
    if (step === "selectRechargeValue") {
      return "Escolha o valor";
    }

    if (step === "waitForPayment") {
      return "Estamos aguardando o pagamento";
    }

    return null;
  }, [step]);

  const currentStep = useMemo(() => {
    if (step === "selectRechargeValue") {
      return <SelectRechargeValue />;
    }

    if (step === "waitForPayment") {
      return <WaitForPayment />;
    }

    if (step === "success") {
      return <Success />;
    }

    return null;
  }, [step]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{currentStepTitle}</DialogTitle>

          <div className="dialog-body">{currentStep}</div>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

function SelectRechargeValue() {
  const form = useForm<RechargeSchema>({
    resolver: zodResolver(rechargeSchema),

    defaultValues: {
      value: 0,
    },
  });
  const { isPending, mutateAsync: createRecharge } = useRechargeMutation();
  const { setStep, setRechargeId, setQrCode, setPayload } =
    useRechargeDialogStore();

  async function onSubmit({ value }: RechargeSchema) {
    const { rechargeId, encodedImage, payload } = await createRecharge({
      body: { value },
    });

    setRechargeId(rechargeId);
    setQrCode(encodedImage);
    setPayload(payload);
    setStep("waitForPayment");
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Form {...form}>
        <DialogDescription>
          Escolha o valor que deseja recarregar e clique em "Recarregar".
        </DialogDescription>

        <div className="border bg-muted/50 rounded-md p-2 text-xs flex items-center gap-2 my-2">
          <img src={Pix} width={21} height={22} />
          Pagamento via Pix
        </div>

        <FormField
          control={form.control}
          name="value"
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <RechargeInput
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            );
          }}
        />
      </Form>

      <DialogFooter>
        <div className="w-full mt-4">
          <Button className="w-full" disabled={isPending}>
            {isPending ? (
              <>
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                Gerando QR Code
              </>
            ) : (
              "Continuar"
            )}
          </Button>
        </div>
      </DialogFooter>
    </form>
  );
}

function WaitForPayment() {
  const { rechargeId, qrCode, payload, setStep } = useRechargeDialogStore();

  useListenRecharge({
    rechargeId,

    onApproved: () => {
      setStep("success");
    },
  });

  if (!qrCode || !payload) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center h-full">
      <DialogDescription>
        <p>Escaneie o QR Code Pix abaixo e faça o pagamento.</p>
      </DialogDescription>

      <div className="pix-qr-code">
        <img
          src={qrCode}
          width={200}
          height={200}
          alt="QR Code para pagamento via Pix"
          className="rounded-xl my-4"
        />
      </div>

      <DialogDescription>
        Ou copie o código abaixo e cole no seu aplicativo de pagamento.
      </DialogDescription>

      <div className="flex justify-between gap-2 w-full mt-2">
        <Input
          defaultValue={payload}
          readOnly
          onFocus={(event) => {
            event.target.select();
          }}
        />

        <ClipboardButton text={payload} />
      </div>
    </div>
  );
}

function Success() {
  const { rechargeId } = useRechargeDialogStore();

  if (!rechargeId) {
    return null;
  }

  return (
    <Suspense fallback={<AnimatedLoading />}>
      <RechargeApproved rechargeId={rechargeId} />
    </Suspense>
  );
}

function RechargeApproved({ rechargeId }: { rechargeId: string }) {
  const { data: recharge } = useRechargeQuery(rechargeId);

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex items-center justify-center bg-green-200 rounded-full h-16 w-16">
        <CheckIcon className="h-6 w-6 text-green-800" />
      </div>

      <DialogTitle>Pagamento aprovado</DialogTitle>

      <DialogDescription className="text-center">
        Seu pagamento foi aprovado com sucesso e já está disponível na sua
        conta.
      </DialogDescription>

      {recharge && recharge.receiptUrl && (
        <Link to={recharge.receiptUrl} target="_blank" className="w-full mt-4">
          <Button variant="secondary" className="w-full">
            <ReceiptIcon className="w-3 h-3 mr-2" />
            Ver recibo
          </Button>
        </Link>
      )}
    </div>
  );
}
